import React from 'react';
import { duckify, filterDuck } from 'utils/duckify';
import {
  fields as fieldsDuck, grades, projectedIncomeReport, schoolYears
} from 'ducks';
import { formatCurrency } from 'utils/i18n';
import classNames from 'classnames';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import moment from 'moment';
import withFiltering from 'hocs/withFiltering';
import { compose } from 'redux';
import { Link, withRouter } from 'react-router';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import ContactList from '../ContactList/ContactList';
import Placeholder from '../Placeholder';
import './styles/ProjectedIncomeReport.scss';
import { ContactRow } from '../ContactList/ContactRows';
import FlashRoot from '../FlashMessage/FlashRoot';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import Filters from '../Filters/Filters';
import MonthYearFilter from '../shared/MonthYearFilter';

const ProjectedIncomeReport = ({
  report, allYears, allGrades, filters, setFilters, additionalFields, allDucksReady
}) => {

  const filterFields = [];
  if (allDucksReady) {
    filterFields.push({
      solr_prompt: 'school_year_id',
      display_name: 'Term',
      options: allYears.map(y => [y.name, y.id])
    },
    {
      solr_prompt: 'grade',
      display_name: 'Grade',
      options: allGrades.map(g => [g.name, g.id])
    });
  }

  return (
    <React.Fragment>
      <div className='income-report-space-between'>
        <h2>Projected Income Report</h2>

        <BulkActions>
          <BulkAction title='Export' icon={faSignOut}>
            <BulkExportAction
              type='ProjectedIncomeReportExportJob'
              location={location}
              url='/api/projected_income_report/export.csv'
            />
            <BulkExportAction
              title='Export to PDF'
              type='ProjectedIncomeReportExportJob'
              location={location}
              url='/api/projected_income_report/export.pdf'
            />
          </BulkAction>
        </BulkActions>
      </div>

      <p>
        Your Projected Income Report downloads will be available&nbsp;
        <Link to='/setup/user_jobs'>here</Link>
        .
      </p>

      <FlashRoot />

      <MonthYearFilter location={location} hideStartDate defaultMonthRange={5} />

      <Filters
        fields={filterFields.concat(additionalFields)}
        selected={filters}
        submitFilters={setFilters}
      />

      <Placeholder.loading
        ready={report && ((report.error && report.errorMessage) || (allDucksReady && report[0]))}
        content={() => {
          if (report.error) {
            return <ContactList contacts={report} />;
          }

          const columns = [
            {
              header: 'Payment Day',
              value: 'payment_day'
            },
            {
              header: 'Payment Category',
              value: 'payment_category',
              columnClassNames: classNames('projected-income-payment-category-col')
            },
            ...report[0].column_headers.map(({ label, key }) => ({
              header: label,
              value: (row) => (
                <td className='contact-list-react-type-currency' key={key}>
                  {formatCurrency((!!row.amount_by_month && row.amount_by_month[key]) || 0)}
                </td>
              ),
              type: 'currency'
            })),
            {
              header: 'Totals',
              value: 'total',
              type: 'currency',
              columnClassNames: classNames('projected-income-billed-total-col')
            }
          ];

          return (
            <ContactList
              columns={columns}
              className='projected-income-list'
              contacts={report[0].report_rows}
              emptyMessage='There are no billing items within the selected date range.'
              makeRow={(props) => {
                const { is_totals_row, payment_category, payment_day } = props.contact;

                return (
                  <ContactRow
                    className={classNames({
                      'projected-income-totals-row': is_totals_row,
                      'projected-income-category-row': !is_totals_row
                    })}
                    key={`${payment_day}-${payment_category}`}
                    {...props}
                  />
                );
              }}
            />
          );
        }}
      />
    </React.Fragment>
  );
};

ProjectedIncomeReport.propTypes = {
  report: PropTypes.arrayOf(PropTypes.shape({
    report_rows: PropTypes.arrayOf(PropTypes.shape({
      amounts_by_month: PropTypes.array,
      is_totals_row: PropTypes.bool,
      payment_day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      payment_category: PropTypes.string,
      total: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })),
    id: PropTypes.string,
    column_headers: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      key: PropTypes.string
    }))
  })),
  allYears: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })),
  allGrades: PropTypes.arrayOf(PropTypes.shape({})),
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),
  setFilters: PropTypes.func,
  additionalFields: PropTypes.arrayOf(PropTypes.object),
  allDucksReady: PropTypes.bool.isRequired
};

ProjectedIncomeReport.defaultProps = {
  report: [],
  allYears: [],
  allGrades: [],
  filters: {},
  setFilters: () => false,
  additionalFields: []
};

const mapDucksToProps = ({ location }) => {
  // default to six months
  const searchParams = location.search ? { ...queryString.parse(location.search) } : {
    end_date: moment().endOf('month').add(5, 'months').format('YYYY-MM-DD')
  };

  return {
    report: filterDuck(projectedIncomeReport, searchParams),
    allYears: schoolYears,
    allGrades: grades,
    additionalFields: filterDuck(fieldsDuck, { for: 'billing' })
  };
};

export default compose(
  withRouter,
  withFiltering,
  duckify(mapDucksToProps)
)(ProjectedIncomeReport);
